/**
 * Centralized route constants for the application.
 * - ADMIN_PATHS: relative paths inside the Admin module (for Route path props).
 * - APP_PATHS: absolute paths for useNavigate(), <Link to={}>, redirects.
 */

// ---------------------------------------------------------------------------
// ADMIN_PATHS – relative paths under /admin (for Route definitions)
// ---------------------------------------------------------------------------

export const ADMIN_PATHS = {
  DASHBOARD: 'dashboard',

  // CSO (Civil Society Organizations)
  CSO: {
    ROOT: 'organizacije/*',
    NEW_OCD: 'organizacije/nova-ocd',
    EDIT: 'organizacije/edit/:id',
  },

  // Institutions
  INSTITUTIONS: {
    ROOT: 'institucije/*',
    NEW: 'institucije/nova',
    DETAIL: 'institucija/:id',
    DETAIL_ALT: 'institucija',
  },

  // Notices
  NOTICES: {
    ROOT: 'objave/*',
    NEW: 'objave/nova',
    DETAIL: 'objave/:id',
    DETAIL_ALT_ROOT: 'objava',
    DETAIL_ALT: 'objava/:id',
  },

  // Users
  USERS: 'korisnici',

  // Obligation (optional param for /obaveza and /obaveza/new)
  OBLIGATION: {
    EDIT: 'obaveza/:obligationId?',
  },

  // Action Plan
  ACTION_PLAN: {
    NEW: 'novi-akcioni-plan',
    LIST: 'akcioni-planovi/*',
    DETAIL: 'akcioni-plan/:actionPlanId',
    REPORT: 'izvestaj/:obligationId',
  },

  NOTIFICATIONS: 'obavestenja',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Build absolute admin path from a relative path string. */
export const getAdminPath = (path) => `/admin/${path}`;

/** Strip trailing /* from path for use in navigate() or <Link to> (e.g. '/admin/organizacije/*' → '/admin/organizacije'). */
export const getAdminNavPath = (path) => (typeof path === 'string' ? path.replace(/\/\*$/, '') : path);

/**
 * Recursively turns an object of relative paths into absolute admin paths.
 * @param {string|object} value - Either a path string or nested object
 * @returns {string|object}
 */
function toAbsoluteAdminPaths(value) {
  if (typeof value === 'string') {
    return getAdminPath(value);
  }
  if (value !== null && typeof value === 'object') {
    const out = {};
    for (const key of Object.keys(value)) {
      out[key] = toAbsoluteAdminPaths(value[key]);
    }
    return out;
  }
  return value;
}

// ---------------------------------------------------------------------------
// APP_PATHS – absolute paths (for navigation and Links)
// ---------------------------------------------------------------------------

export const LOGIN_LAUNCH_PATH = '/prijava';

export const APP_PATHS = {
  AUTH: {
    LOGIN: LOGIN_LAUNCH_PATH,
  },
  ADMIN: {
    ROOT: '/admin',
    ...toAbsoluteAdminPaths(ADMIN_PATHS),
  },
};

/** Build obligation edit URL (admin). Optional query: actionPlanId. */
export const getAdminObligationPath = (obligationId, actionPlanId = null) => {
  const base = `/admin/obaveza/${obligationId === 'new' ? 'new' : obligationId}`;
  if (actionPlanId) return `${base}?actionPlanId=${actionPlanId}`;
  return base;
};

/** Build obligation report form URL (admin). Optional query: actionPlanId. */
export const getAdminObligationReportPath = (obligationId, actionPlanId = null) => {
  const base = `/admin/izvestaj/${obligationId}`;
  if (actionPlanId) return `${base}?actionPlanId=${actionPlanId}`;
  return base;
};

/** Build action plan detail URL (admin). */
export const getAdminActionPlanDetailPath = (actionPlanId, query = {}) => {
  const base = `/admin/akcioni-plan/${actionPlanId}`;
  const params = new URLSearchParams(query);
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
};

/** Dedicated admin page for creating a new action plan. */
export const getAdminNewActionPlanPath = () => APP_PATHS.ADMIN.ACTION_PLAN.NEW;

// ---------------------------------------------------------------------------
// Public (non-admin) route helpers – DRY, no hardcoded path strings
// ---------------------------------------------------------------------------

/** Public list path (for pathname checks and navigation). */
export const PUBLIC_ACTION_PLAN_LIST_PATH = '/akcioni-planovi';

/** Public detail path prefix (for pathname.startsWith checks). */
export const PUBLIC_ACTION_PLAN_DETAIL_PREFIX = '/akcioni-plan/';

/** Public list of action plans. */
export const getPublicActionPlanListPath = () => PUBLIC_ACTION_PLAN_LIST_PATH;

/** Public action plan detail. queryParams: e.g. { tab: 'obaveze' }. */
export const getPublicActionPlanDetailPath = (id, queryParams = {}) => {
  const qs = new URLSearchParams(queryParams).toString();
  return `/akcioni-plan/${id}${qs ? `?${qs}` : ''}`;
};
